/**
 * PUBLIC ITEM SEARCH COMPONENT
 * 
 * Searches across all public collections to help users find existing items
 * and pre-fill their Add forms with consistent data.
 * 
 * Features:
 * - Two-field search (Brand + Name) for precise matching
 * - Debounced search across public collections
 * - Shows how many collectors have each item
 * - Click to pre-fill form fields
 * - Skips current user's items (they're adding something new!)
 */

import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, Users, ChevronRight, X } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCurrentUser } from '../../firebase/auth';

// Exported type for use in other components
export type SearchResult = {
  // Basic info
  brand: string;
  name: string;
  itemType: string;

  // Polish-specific
  category?: string;
  colorDescription?: string;
  colorFamily?: string[];
  colors?: string[];
  baseType?: string;
  finish?: string[];
  holoType?: string[];
  colorShift?: string;
  particles?: string[];
  specialFeatures?: string[];
  utility?: string[];
  colorCharacter?: string[];
  line?: string;
  swatchNumber?: string;

  // Plate-specific
  plateSize?: string;
  numberOfImages?: number;
  plateType?: string;

  // Other fields
  collectionName?: string;

  // Photo for preview
  photoUrl?: string;

  // Meta
  collectorCount: number;
};

interface PublicItemSearchProps {
  itemType: 'polish' | 'plate' | 'tool' | 'embellishment' | 'transfer' | 'powder' | 'acrylic' | 'cuticle_care';
  onSelect: (item: SearchResult) => void;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

export default function PublicItemSearch({ itemType, onSelect }: PublicItemSearchProps) {
  const [brandSearch, setBrandSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const debouncedBrand = useDebounce(brandSearch, 300);
  const debouncedName = useDebounce(nameSearch, 300);
  
  // Search function
  const performSearch = useCallback(async (brand: string, name: string) => {
    // Need at least brand OR name with 2+ characters
    const brandTrimmed = brand.trim().toLowerCase();
    const nameTrimmed = name.trim().toLowerCase();
    
    if (brandTrimmed.length < 2 && nameTrimmed.length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const currentUser = getCurrentUser();
      const itemMap = new Map<string, SearchResult & { count: number }>();
      
      // Get all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      for (const userDoc of usersSnapshot.docs) {
        // Skip current user - they're adding something NEW
        if (currentUser && userDoc.id === currentUser.uid) continue;
        
        const userData = userDoc.data();
        
        // Skip private profiles
        if (userData.privacy?.profileVisibility === 'private') continue;
        
        // Query their items
        const itemsRef = collection(db, 'users', userDoc.id, 'items');
        const itemsQuery = query(
          itemsRef,
          where('itemType', '==', itemType),
          limit(500) // Higher limit since we're filtering more precisely
        );
        
        try {
          const itemsSnapshot = await getDocs(itemsQuery);
          
          for (const itemDoc of itemsSnapshot.docs) {
            const item = itemDoc.data();
            
            // Skip destashed items
            if (item.isDestashed === true) continue;
            
            // Check brand match (if searching by brand)
            const itemBrand = (item.brand || '').toLowerCase();
            const itemName = (item.name || '').toLowerCase();
            
            const brandMatches = brandTrimmed.length < 2 || itemBrand.includes(brandTrimmed);
            const nameMatches = nameTrimmed.length < 2 || itemName.includes(nameTrimmed);
            
            if (brandMatches && nameMatches) {
              const key = `${itemBrand.trim()}|${itemName.trim()}`;
              
              if (itemMap.has(key)) {
                const existing = itemMap.get(key)!;
                existing.count++;
                existing.collectorCount = existing.count;
              } else {
                // Get photo URL
                let photoUrl = '';
                if (item.photos?.length > 0) {
                  const defaultPhoto = item.photos.find((p: any) => p.isDefault);
                  photoUrl = defaultPhoto?.url || item.photos[0].url;
                }
                
                itemMap.set(key, {
                  brand: item.brand || '',
                  name: item.name || '',
                  itemType: item.itemType,
                  category: item.category,
                  colorDescription: item.colorDescription,
                  colorFamily: item.colorFamily,
                  colors: item.colors,
                  baseType: item.baseType,
                  finish: item.finish,
                  holoType: item.holoType,
                  colorShift: item.colorShift,
                  particles: item.particles,
                  specialFeatures: item.specialFeatures,
                  utility: item.utility,
                  colorCharacter: item.colorCharacter,
                  line: item.line,
                  swatchNumber: item.swatchNumber,
                  plateSize: item.plateSize,
                  numberOfImages: item.numberOfImages,
                  collectionName: item.collectionName,
                  photoUrl,
                  collectorCount: 1,
                  count: 1
                });
              }
            }
          }
        } catch (err) {
        }
      }
      
      // Convert to array and sort by collector count
      const resultsArray = Array.from(itemMap.values())
        .sort((a, b) => b.collectorCount - a.collectorCount)
        .slice(0, 15); // Show more results with precise search
      
      setResults(resultsArray);
    } catch (error) {
      console.error('Error searching public items:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [itemType]);
  
  // Effect to trigger search
  useEffect(() => {
    performSearch(debouncedBrand, debouncedName);
  }, [debouncedBrand, debouncedName, performSearch]);
  
  const handleSelect = (result: SearchResult) => {
    onSelect(result);
    setBrandSearch('');
    setNameSearch('');
    setResults([]);
    setHasSearched(false);
  };
  
  const clearSearch = () => {
    setBrandSearch('');
    setNameSearch('');
    setResults([]);
    setHasSearched(false);
  };
  
  const getItemTypeEmoji = () => {
    switch (itemType) {
      case 'polish': return '💅';
      case 'plate': return '🎨';
      case 'tool': return '🔧';
      case 'embellishment': return '💎';
      case 'transfer': return '🌸';
      case 'powder': return '✨';
      case 'acrylic': return '🎭';
      default: return '📦';
    }
  };
  
  const hasInput = brandSearch.trim().length > 0 || nameSearch.trim().length > 0;
  
  return (
    <div className="space-y-3 p-4 bg-purple-50 rounded-xl border border-purple-200">
      {/* Section header */}
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-5 h-5 text-purple-600" />
        <h3 className="font-bold text-purple-800 font-body">Search to Add an Item</h3>
      </div>
      <p className="text-xs text-purple-600 -mt-1 mb-3 font-body">
        Find an existing item to copy its details, or skip and enter manually below
      </p>

      {/* Two-field search */}
      <div className="grid grid-cols-2 gap-3">
        {/* Brand field */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1 font-body">
            Brand
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder="e.g., Tonic"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-body text-gray-900 bg-white"
            />
          </div>
        </div>

        {/* Name field */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1 font-body">
            Name
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="e.g., Marry Me Blu"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-body text-gray-900 bg-white"
            />
          </div>
        </div>
      </div>
      
      {/* Clear button */}
      {hasInput && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={clearSearch}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <X className="icon-xs" />
            Clear search
          </button>
        </div>
      )}
      
      {/* Loading indicator */}
      {isSearching && (
        <div className="flex items-center gap-2 text-sm text-purple-600">
          <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
          <span className="font-body">Searching collections...</span>
        </div>
      )}
      
      {/* Results */}
      {!isSearching && hasSearched && (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-lg max-h-64 overflow-y-auto">
          {results.length > 0 ? (
            <>
              <div className="px-3 py-2 bg-gray-100 border-b border-gray-200">
                <p className="text-xs text-gray-600 font-body">
                  Found in {results.reduce((sum, r) => sum + r.collectorCount, 0)} collection{results.reduce((sum, r) => sum + r.collectorCount, 0) !== 1 ? 's' : ''}:
                </p>
              </div>
              {results.map((result, index) => (
                <button
                  key={`${result.brand}-${result.name}-${index}`}
                  type="button"
                  onClick={() => handleSelect(result)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 transition-default text-left border-b border-gray-100 last:border-b-0"
                >
                  {/* Photo or emoji */}
                  {result.photoUrl ? (
                    <img
                      src={result.photoUrl}
                      alt={result.name}
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                      <span>{getItemTypeEmoji()}</span>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate font-body">
                      {result.name}
                    </p>
                    <p className="text-xs text-gray-600 truncate font-body">
                      {result.brand}
                    </p>
                  </div>

                  {/* Collector count */}
                  <div className="flex items-center gap-1 text-gray-500 flex-shrink-0">
                    <Users className="w-3.5 h-3.5" />
                    <span className="text-xs">{result.collectorCount}</span>
                  </div>

                  <ChevronRight className="icon-sm text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </>
          ) : (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-gray-500 font-body">
                No matches found. You'll be the first to add this!
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Helper text */}
      {!hasSearched && !hasInput && (
        <p className="text-xs text-purple-600 font-body">
          💡 Search by brand and/or name to find existing items and copy their info
        </p>
      )}
    </div>
  );
}
